// Config panel for the "extract_params" action block
// Extracts variables from webhook trigger data (URL path, query params, body) for downstream use
import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Alert,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  FormHelperText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

const SOURCES = [
  { value: 'path', label: 'URL Path Segment', description: 'Extract a segment from the webhook URL path by position' },
  { value: 'body', label: 'Request Body Field', description: 'Extract a field from the JSON request body' },
  { value: 'query', label: 'Query Parameter', description: 'Extract a URL query parameter' },
];

const ExtractParamsConfig = ({ config, onChange, onSummaryChange }) => {
  const extractions = config.extractions || [{ source: 'path', key: '', variableName: '', segmentIndex: '' }];

  const updateExtractions = (newExtractions) => {
    const updated = { ...config, extractions: newExtractions };
    onChange(updated);
    const named = newExtractions.filter(e => e.variableName).map(e => e.variableName);
    onSummaryChange(named.length > 0 ? named.join(', ') : 'No variables defined');
  };

  const handleEntryChange = (index, field, value) => {
    const newEntries = extractions.map((e, i) =>
      i === index ? { ...e, [field]: value } : e
    );
    updateExtractions(newEntries);
  };

  const addExtraction = () => {
    updateExtractions([...extractions, { source: 'path', key: '', variableName: '', segmentIndex: '' }]);
  };

  const removeExtraction = (index) => {
    if (extractions.length <= 1) return;
    updateExtractions(extractions.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Extracts values from the incoming webhook request and stores them as
        variables. Downstream blocks can access these via the flow context.
      </Typography>

      <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          <strong>Example:</strong> A webhook call to{' '}
          <code style={{ fontSize: '0.7rem' }}>/webhook/my-endpoint/123?format=json</code>{' '}
          with body <code style={{ fontSize: '0.7rem' }}>{'{"action":"on"}'}</code>
          <br />
          &bull; Path segment 0 = <code style={{ fontSize: '0.7rem' }}>123</code> (parts after the endpoint path)
          <br />
          &bull; Query param <code style={{ fontSize: '0.7rem' }}>format</code> = <code style={{ fontSize: '0.7rem' }}>json</code>
          <br />
          &bull; Body field <code style={{ fontSize: '0.7rem' }}>action</code> = <code style={{ fontSize: '0.7rem' }}>on</code>
        </Typography>
      </Alert>

      {extractions.map((entry, index) => (
        <Paper key={index} variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" fontWeight={600}>Variable {index + 1}</Typography>
            {extractions.length > 1 && (
              <IconButton size="small" onClick={() => removeExtraction(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel>Source</InputLabel>
            <Select
              value={entry.source || 'path'}
              onChange={(e) => handleEntryChange(index, 'source', e.target.value)}
              label="Source"
            >
              {SOURCES.map(s => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {SOURCES.find(s => s.value === (entry.source || 'path'))?.description}
            </FormHelperText>
          </FormControl>

          {entry.source === 'path' && (
            <TextField
              size="small"
              label="Path Segment Index"
              type="number"
              value={entry.segmentIndex ?? 0}
              onChange={(e) => handleEntryChange(index, 'segmentIndex', parseInt(e.target.value, 10) || 0)}
              helperText="0-based index of the URL segment after the webhook endpoint path"
              inputProps={{ min: 0 }}
            />
          )}

          {entry.source === 'body' && (
            <TextField
              size="small"
              label="Field Path"
              value={entry.key || ''}
              onChange={(e) => handleEntryChange(index, 'key', e.target.value)}
              helperText='JSON field name. Use dot notation for nested fields (e.g. "data.locationId")'
              placeholder="e.g. locationId or data.value"
            />
          )}

          {entry.source === 'query' && (
            <TextField
              size="small"
              label="Query Parameter Name"
              value={entry.key || ''}
              onChange={(e) => handleEntryChange(index, 'key', e.target.value)}
              helperText="Name of the URL query parameter"
              placeholder="e.g. format"
            />
          )}

          <TextField
            size="small"
            label="Variable Name"
            value={entry.variableName || ''}
            onChange={(e) => handleEntryChange(index, 'variableName', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
            required
            helperText="Name to store the value as (letters, numbers, underscores)"
            placeholder="e.g. locationId"
          />
        </Paper>
      ))}

      <Button size="small" startIcon={<AddIcon />} onClick={addExtraction} variant="outlined">
        Add Variable
      </Button>

      {extractions.some(e => e.variableName) && (
        <Alert severity="success" variant="outlined" sx={{ py: 0.5 }}>
          <Typography variant="caption">
            <strong>Available in downstream blocks:</strong><br />
            {extractions.filter(e => e.variableName).map(e => (
              <span key={e.variableName}>
                <code style={{ fontSize: '0.7rem' }}>context.variables.{e.variableName}</code><br />
              </span>
            ))}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default ExtractParamsConfig;
